import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarClock, History, Webhook } from "lucide-react";

import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { DataPagination } from "@/shared/components/DataPagination";
import { ListFilterToolbar } from "@/shared/components/ListFilterToolbar";
import { usePagination } from "@/shared/hooks/usePagination";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";

import { JobsService } from "../services/jobsService";
import type { Job, JobRunRow, JobRunsPeriod, JobRunsQuery, JobRunsStatusFilter } from "../types/job";
import { LIVE_RUN_STATUSES, formatDuration } from "../utils/runs";
import { JobRunSheet } from "./JobRunSheet";
import { JobRunStatusBadge } from "./JobRunStatusBadge";

interface JobRunsTabProps {
  productId: string;
  /** Os jobs do workspace, para o filtro por job. */
  jobs: Job[];
  /** O SSE está entregando as mudanças: a consulta periódica para. */
  realtime: boolean;
}

const EMPTY_QUERY: JobRunsQuery = { status: "all", period: "7d", jobIds: [], q: "" };
const LIVE_REFRESH_MS = 10_000;
const PAGE_SIZE = 20;

/**
 * O histórico geral dos jobs (AB#86605, task 86869): as execuções de todos os
 * jobs do workspace numa lista só. Filtra no servidor — a lista é paginada, e
 * filtrar só a página carregada esconderia o que está nas outras.
 */
export function JobRunsTab({ productId, jobs, realtime }: JobRunsTabProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split("-")[0];
  const [filter, setFilter] = useState<JobRunsQuery>(EMPTY_QUERY);
  const [open, setOpen] = useState<JobRunRow | null>(null);
  const search = useDebouncedValue(filter.q);
  const query = useMemo(() => ({ ...filter, q: search }), [filter, search]);
  const { currentPage, pageSize, setCurrentPage, setPageSize } = usePagination({ initialPageSize: PAGE_SIZE });

  // Filtro novo, lista nova: volta para a primeira página, senão a pessoa
  // ficaria numa página que o filtro pode nem ter mais.
  useEffect(() => setCurrentPage(1), [query, setCurrentPage]);

  // Paginada no servidor, como o padrão das listas: o histórico cresce a cada
  // disparo, e trazer tudo para paginar na tela não escala.
  const runsQuery = useQuery({
    queryKey: ["job-runs", productId, query, currentPage, pageSize],
    queryFn: () => JobsService.runs(productId, query, currentPage, pageSize),
    // A página anterior fica na tela enquanto a próxima chega, sem piscar.
    placeholderData: (previous) => previous,
    // Reserva do SSE: só consulta de tempo em tempo quando o aviso não está
    // chegando e há execução em andamento.
    refetchInterval: (q) =>
      !realtime && q.state.data?.runs.some((run) => LIVE_RUN_STATUSES.has(run.status)) ? LIVE_REFRESH_MS : false,
  });

  const runs = runsQuery.data?.runs ?? [];
  const totalRuns = runsQuery.data?.total ?? 0;
  const clean =
    filter.q.trim() === "" && filter.status === "all" && filter.period === EMPTY_QUERY.period && filter.jobIds.length === 0;

  // O SelectTrigger do projeto vem num `div` com `w-full`: sem o contêiner de
  // largura fixa, o seletor toma a linha inteira e cai para baixo da busca.
  const periodSelect = (
    <div className="w-[150px] shrink-0">
    <Select value={filter.period} onValueChange={(period) => setFilter({ ...filter, period: period as JobRunsPeriod })}>
      <SelectTrigger className="h-9">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(["24h", "7d", "30d", "all"] as const).map((period) => (
          <SelectItem key={period} value={period}>
            {t(`jobs.runs.period.${period}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    </div>
  );

  return (
    <div className="space-y-6">
      <ListFilterToolbar<JobRunsStatusFilter, string>
        query={filter.q}
        onQueryChange={(q) => setFilter({ ...filter, q })}
        searchPlaceholder={t("jobs.runs.searchPlaceholder")}
        clearSearchLabel={t("jobs.filter.clearSearch")}
        modes={[
          { id: "all", label: t("jobs.runs.filter.all") },
          { id: "running", label: t("jobs.runs.filter.running") },
          { id: "completed", label: t("jobs.runs.filter.completed") },
          { id: "failed", label: t("jobs.runs.filter.failed") },
        ]}
        mode={filter.status}
        onModeChange={(status) => setFilter({ ...filter, status })}
        optionsLabel={t("jobs.runs.filter.job")}
        options={jobs.map((job) => ({ id: job.id, label: job.name }))}
        selected={filter.jobIds}
        onToggleOption={(id) =>
          setFilter({
            ...filter,
            jobIds: filter.jobIds.includes(id) ? filter.jobIds.filter((j) => j !== id) : [...filter.jobIds, id],
          })
        }
        extra={periodSelect}
        clean={clean}
        onClear={() => setFilter(EMPTY_QUERY)}
        clearLabel={t("jobs.filter.clear")}
        total={runsQuery.isLoading ? -1 : runs.length}
        noResultsLabel={t("jobs.runs.noResults")}
      />

      {runsQuery.isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : runsQuery.isError ? (
        <p className="text-sm text-destructive">{t("jobs.runs.loadFailed")}</p>
      ) : runs.length === 0 ? (
        clean && (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <History className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-medium">{t("jobs.runs.empty.title")}</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t("jobs.runs.empty.description")}</p>
          </div>
        )
      ) : (
        <div className="space-y-3">
          <div className="rounded-lg border border-border/60">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[18%]">{t("jobs.runs.columns.job")}</TableHead>
                  <TableHead>{t("jobs.runs.columns.status")}</TableHead>
                  <TableHead>{t("jobs.runs.columns.trigger")}</TableHead>
                  <TableHead className="whitespace-nowrap">{t("jobs.runs.columns.started")}</TableHead>
                  <TableHead>{t("jobs.runs.columns.duration")}</TableHead>
                  <TableHead className="w-[34%]">{t("jobs.runs.columns.summary")}</TableHead>
                  <TableHead className="text-center">{t("jobs.runs.columns.writes")}</TableHead>
                  <TableHead className="text-center">{t("jobs.runs.columns.pending")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.execution_id} className="cursor-pointer" onClick={() => setOpen(run)}>
                    <TableCell className="max-w-0">
                      <p className="truncate font-medium" title={run.job.name}>
                        {run.job.name}
                      </p>
                    </TableCell>
                    <TableCell>
                      <JobRunStatusBadge status={run.status} />
                    </TableCell>
                    <TableCell>
                      {run.trigger_type ? (
                        <span className="flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground">
                          {run.trigger_type === "webhook" ? (
                            <Webhook className="h-3.5 w-3.5" />
                          ) : (
                            <CalendarClock className="h-3.5 w-3.5" />
                          )}
                          {t(`jobs.runs.trigger.${run.trigger_type}`)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="whitespace-nowrap text-sm text-muted-foreground"
                      title={run.started_at ? new Date(run.started_at).toLocaleString(i18n.language) : undefined}
                    >
                      {run.started_at
                        ? formatDistanceToNow(new Date(run.started_at), {
                            addSuffix: true,
                            locale: locale === "pt" ? ptBR : undefined,
                          })
                        : "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {formatDuration(run.duration_ms)}
                    </TableCell>
                    <TableCell className="max-w-0">
                      <p className="line-clamp-2 text-sm text-muted-foreground" title={run.summary ?? run.error ?? undefined}>
                        {run.summary ?? run.error ?? "—"}
                      </p>
                    </TableCell>
                    <TableCell className="text-center text-sm">{run.writes}</TableCell>
                    <TableCell className="text-center text-sm">
                      <span className={run.pending_count > 0 ? "font-medium text-amber-600 dark:text-amber-400" : ""}>
                        {run.pending_count}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DataPagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalRuns}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            isLoading={runsQuery.isFetching}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      )}

      <JobRunSheet productId={productId} row={open} realtime={realtime} onClose={() => setOpen(null)} />
    </div>
  );
}
